import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Phone } from "lucide-react";
import api from "@/lib/api";
import { Markdown } from "@/components/Markdown";

/** An operator's own blog, on their booking site — the SEO surface they control. */
export default function OperatorBlog() {
  const { companyId, slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api.get(slug ? `/public/company/${companyId}/blog/${slug}`
                 : `/public/company/${companyId}/blog`)
      .then((res) => setData(res.data))
      .catch(() => setError("That page is not here."));
  }, [companyId, slug]);

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900 px-6 text-center">
        <div>
          <p className="text-sm text-slate-400">{error}</p>
          <Link to={`/book/${companyId}`} className="mt-4 inline-block text-sm text-gold hover:underline">
            Book a car
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const { company, post, posts } = data;

  return (
    <div data-testid="operator-blog" className="min-h-screen bg-ink-900 px-5 py-14 sm:px-10 lg:px-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to={`/book/${companyId}`} className="font-display text-lg text-slate-100">
            {company.name}
          </Link>
          <div className="flex items-center gap-3">
            {company.phone && (
              <a href={`tel:${company.phone}`}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-gold">
                <Phone className="h-3.5 w-3.5" /> {company.phone}
              </a>
            )}
            <Link data-testid="operator-blog-book" to={`/book/${companyId}`}
              className="rounded-full bg-gold px-4 py-2 text-sm font-semibold text-ink-900">
              Book a car
            </Link>
          </div>
        </div>

        {post ? (
          <article className="mt-12">
            <Link data-testid="operator-blog-back" to={`/book/${companyId}/blog`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-gold">
              <ArrowLeft className="h-3.5 w-3.5" /> All posts
            </Link>
            <h1 className="mt-6 font-display text-4xl leading-tight text-slate-50 sm:text-5xl">
              {post.title}
            </h1>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">
              {(post.published_at || "").slice(0, 10)}
            </p>
            {post.cover_url && (
              <img data-testid="operator-blog-cover-image" src={post.cover_url} alt=""
                className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover" />
            )}
            <div className="mt-8"><Markdown text={post.body} /></div>
          </article>
        ) : (
          <>
            <h1 className="mt-12 font-display text-4xl leading-tight text-slate-50 sm:text-5xl">
              From the {company.name} desk.
            </h1>
            <div className="mt-12 space-y-3">
              {posts.map((p) => (
                <Link key={p.id} data-testid={`operator-blog-card-${p.slug}`}
                  to={`/book/${companyId}/blog/${p.slug}`}
                  className="block overflow-hidden rounded-2xl border border-ink-600 bg-ink-800/60 transition-colors duration-200 hover:border-gold/50">
                  {p.cover_url && (
                    <img src={p.cover_url} alt="" data-testid={`operator-blog-card-cover-${p.slug}`}
                      className="aspect-[16/9] w-full object-cover" />
                  )}
                  <div className="p-6">
                    <p className="font-display text-xl text-slate-100">{p.title}</p>
                    <p className="mt-2 text-sm text-slate-400">{p.excerpt}</p>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {(p.published_at || "").slice(0, 10)}
                    </p>
                  </div>
                </Link>
              ))}
              {posts.length === 0 && (
                <p data-testid="operator-blog-none" className="text-sm text-slate-500">
                  Nothing published yet.
                </p>
              )}
            </div>
          </>
        )}

        <footer data-testid="operator-blog-footer" className="mt-20 border-t border-ink-600 pt-8">
          <p className="font-display text-xl text-slate-100">Need a car?</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <Link data-testid="operator-blog-footer-book" to={`/book/${companyId}`}
              className="rounded-full bg-gold px-5 py-2.5 font-semibold text-ink-900">
              Book with {company.name}
            </Link>
            {company.phone && (
              <a href={`tel:${company.phone.replace(/[^\d+]/g, "")}`}
                data-testid="operator-blog-footer-phone"
                className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold">
                <Phone className="h-3.5 w-3.5" /> {company.phone}
              </a>
            )}
            {company.email && (
              <a href={`mailto:${company.email}`} data-testid="operator-blog-footer-email"
                className="inline-flex items-center gap-1.5 break-all text-slate-400 hover:text-gold">
                <Mail className="h-3.5 w-3.5" /> {company.email}
              </a>
            )}
            <Link to={`/book/${companyId}/blog`} className="text-slate-400 hover:text-gold">
              All posts
            </Link>
          </div>
          {company.hours && (
            <p data-testid="operator-blog-footer-hours" className="mt-3 text-xs text-slate-500">
              {company.hours}
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}
